import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import heroImage from '../assets/images/hero-custom.jpg';

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <div className="relative w-full overflow-hidden bg-rich-black">
      {/* Background Image Container */}
      <div className="relative w-full">
        <motion.div 
          className="w-full"
          style={{ y }}
        >
          <img
            src={heroImage}
            alt="University Community"
            className="w-full h-auto block"
            style={{ minHeight: '60vh', objectFit: 'cover' }} 
          />
        </motion.div>
        
        {/* Gradient Overlay for Text Readability */}
        <div className="absolute inset-0 bg-black/50 pointer-events-none" />

        <div className="absolute inset-0 bg-gradient-to-t from-rich-black via-rich-black/50 to-transparent pointer-events-none" />
      </div>
      {/* Content Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            style={{ opacity }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto text-center flex flex-col items-center"
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-vibrant-red/90 text-white backdrop-blur-sm mb-6 shadow-lg border border-vibrant-red/30"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              #1 Community for Students
            </motion.div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-tight mb-6 drop-shadow-lg">
              Unlock Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-vibrant-red to-dark-red">Potential</span>
              <br />With CampusQube</h1>

            <p className="mt-4 text-lg sm:text-xl text-light-gray max-w-2xl mx-auto mb-10 leading-relaxed drop-shadow-md">
              Join the exclusive community for university students. Get access to extracurricular activities, VIP mentorship, and guidelines to become the top 1% in your field.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/signin"
                  className="w-full sm:w-auto flex items-center justify-center px-8 py-4 border border-transparent text-lg font-bold rounded-xl text-white bg-button-gradient hover:shadow-glow transition-all duration-300"
                >
                  Join Us Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/opportunities"
                  className="w-full sm:w-auto flex items-center justify-center px-8 py-4 border-2 border-white/30 text-lg font-bold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 hover:border-white/50 shadow-lg transition-all duration-300"
                >
                  Explore Opportunities
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
